import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, Form, Image, Link, Callout, Text } from "@wayflyer/flyui";
import { RhfTextField } from "@wayflyer/flyui-hook-form";
import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import type { MessageDescriptor } from "react-intl";
import { FormattedMessage, defineMessage, useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";

import { AuthCallbackPath } from "../../callback-paths";
import { usePlatformConnectorProperties } from "../../queries";
import { useConnectionFlowsConfig } from "../../config";
import type { ConnectionPropertiesSchema, ConnectorPropertiesSchema } from "../../../data-sources/schemas";
import type { MagentoData } from "../../common/connect-by-post";
import { useConnectByPost } from "../../common/connect-by-post";
import { ConnectionsList } from "../../common/connections";
import { OAuthErrorBanner } from "../../common/oauth-error-banner";
import { MESSAGES } from "../../messages";

const DEFAULT_URL_HINT_ERROR = defineMessage({
  id: "funding_application: valid magento URL hint",
  defaultMessage: "Please enter a valid Magento store url.",
});

const DEFAULT_TOKEN_ERROR = defineMessage({
  id: "funding_application: valid magento access token",
  defaultMessage: "Please enter a valid Magento access token.",
});

const MAGENTO_403_ERROR_KEY = "magento 403 hint";

const MAGENTO_403_ERROR = defineMessage({
  id: "funding-application: magento 403 hint",
  defaultMessage: "We couldn't connect to your store. Your firewall may be blocking our access.",
});

const MAGENTO_SERVER_VALIDATION_ERRORS: Map<string, MessageDescriptor> = new Map([
  ["valid URL hint", DEFAULT_URL_HINT_ERROR],
  ["valid bigcommerce access token hint", DEFAULT_TOKEN_ERROR],
  [MAGENTO_403_ERROR_KEY, MAGENTO_403_ERROR],
]);

const CONNECTOR_SLUG = "magento2";
const stripProtocol = (url: string): string => url.replace(/^https?:\/\//, "");

const MagentoConnectForm = ({
  targetPath,
  connectorProperties,
  initialStoreName,
  onInitiateConnection,
}: {
  targetPath: string | null | undefined;
  connectorProperties: ConnectorPropertiesSchema;
  initialStoreName?: string | null;
  onInitiateConnection?: () => void;
}) => {
  const intl = useIntl();
  const connectByPost = useConnectByPost();
  const [showForbiddenBanner, setShowForbiddenBanner] = useState(false);

  const schema = Yup.object({
    storeName: Yup.string()
      .trim()
      .required(intl.formatMessage(DEFAULT_URL_HINT_ERROR))
      .transform((value: string) => {
        if (value.startsWith("http://") || value.startsWith("https://")) {
          return value;
        } else {
          return "https://" + value;
        }
      })
      .url(intl.formatMessage(DEFAULT_URL_HINT_ERROR)),
    magentoAccessToken: Yup.string().trim().required(intl.formatMessage(DEFAULT_TOKEN_ERROR)),
  });

  const magentoForm = useForm<MagentoData>({
    resolver: yupResolver(schema),
    defaultValues: { storeName: initialStoreName ? stripProtocol(initialStoreName) : "" },
  });

  const connectHandler = async (formData: MagentoData) => {
    if (targetPath) {
      onInitiateConnection?.();
      setShowForbiddenBanner(false);
      const response = await connectByPost(targetPath, formData);
      if (response?.errorMessage && response?.errorField) {
        if (response.errorMessage === MAGENTO_403_ERROR_KEY) {
          setShowForbiddenBanner(true);
        }
        if (response.errorField === "storeName") {
          magentoForm.clearErrors();
          magentoForm.setError("storeName", {
            type: "validate",
            message: intl.formatMessage(
              MAGENTO_SERVER_VALIDATION_ERRORS.get(response.errorMessage) ?? DEFAULT_URL_HINT_ERROR
            ),
          });
        } else if (response.errorField === "magentoAccessToken") {
          magentoForm.clearErrors();
          magentoForm.setError("magentoAccessToken", {
            type: "validate",
            message: intl.formatMessage(
              MAGENTO_SERVER_VALIDATION_ERRORS.get(response.errorMessage) ?? DEFAULT_TOKEN_ERROR
            ),
          });
        }
      }
    }
  };

  return (
    <Flex direction="column" gap="4">
      {showForbiddenBanner && (
        <Callout
          variant="warning"
          description={intl.formatMessage(
            {
              id: "funding-application: magento 403 banner",
              defaultMessage:
                "We couldn't reach your store. Your firewall or security settings may be blocking our access. Please <link>configure your store to allow API calls from Wayflyer</link>.",
            },
            {
              link: (chunks: ReactNode) => (
                <Link
                  url="https://help-center.wayflyer.com/en/articles/268930-how-to-configure-your-self-hosted-store-to-allowlist-api-calls-from-wayflyer"
                  external
                >
                  {chunks}
                </Link>
              ),
            }
          )}
        />
      )}
      <Form
        onSubmit={magentoForm.handleSubmit(connectHandler)}
        primaryActionVariant="Tertiary"
        primaryAction={{
          content: intl.formatMessage({
            id: "funding-application: log in to magento",
            defaultMessage: "Log in to Magento",
          }),
          loading: magentoForm.formState.isSubmitting,
        }}
      >
        <RhfTextField
          label={intl.formatMessage({
            id: "funding-application: enter magento store url",
            defaultMessage: "Enter store url",
          })}
          labelIcon={
            connectorProperties.connectionLogoSrc ? <Image src={connectorProperties.connectionLogoSrc} /> : undefined
          }
          name="storeName"
          valuePrefix="https://"
          form={magentoForm}
          disabled={magentoForm.formState.isSubmitting}
        />
        <RhfTextField
          label={intl.formatMessage({
            id: "funding-application: enter magento access token",
            defaultMessage: "Enter access token",
          })}
          helperText={{
            text: `[${intl.formatMessage({
              id: "funding-application: get magento access token",
              defaultMessage: "Get an access token",
            })}](https://help-center.wayflyer.com/en/articles/96284-how-do-i-connect-my-magento-store)`,
            asMarkdown: true,
          }}
          name="magentoAccessToken"
          form={magentoForm}
          disabled={magentoForm.formState.isSubmitting}
        />
        <FormattedMessage
          id="funding-application: codat third party disclaimer"
          defaultMessage="<p>We use a third party service called <a>Codat</a> to connect to and access your data</p>"
          values={{
            p: (chunks: UIElement[]) => <Text color="placeholder">{chunks}</Text>,
            a: (chunks: UIElement[]) => (
              <Link url="https://www.codat.io/powered-by-codat/" external>
                {chunks}
              </Link>
            ),
          }}
        />
      </Form>
    </Flex>
  );
};

interface MagentoConnectProps {
  postConnectPath: AuthCallbackPath;
  onInitiateConnection?: () => void;
}

export const MagentoConnect = ({ postConnectPath, onInitiateConnection }: MagentoConnectProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { connectorsApiUrl } = useConnectionFlowsConfig();
  const { data: connectorProperties } = usePlatformConnectorProperties(
    customerDomain,
    CONNECTOR_SLUG,
    postConnectPath,
    connectorsApiUrl
  );

  return (
    <Flex direction="column" justify="flex-start">
      <Flex direction="column" gap="8">
        <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
        <Text>{intl.formatMessage(MESSAGES.ecommerceDataUsage, { platform: "Magento" })}</Text>
        <ConnectionsList
          connectorProperties={connectorProperties}
          renderConnectAnother={(onConnectAnother) => (
            <Text size="sm">
              {intl.formatMessage(MESSAGES.connectAnotherStore, {
                link: (chunks: ReactNode) => <Link onClick={onConnectAnother}>{chunks}</Link>,
              })}
            </Text>
          )}
          connectForm={
            <MagentoConnectForm
              targetPath={connectorProperties.postPath}
              connectorProperties={connectorProperties}
              onInitiateConnection={onInitiateConnection}
            />
          }
          renderReconnectForm={(connection: ConnectionPropertiesSchema) => (
            <MagentoConnectForm
              targetPath={connection.reconnectPostPath}
              connectorProperties={connectorProperties}
              initialStoreName={connection.identifierName}
              onInitiateConnection={onInitiateConnection}
            />
          )}
        />
      </Flex>
    </Flex>
  );
};
